import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Connection,
  EdgeChange,
  NodeChange,
} from '@xyflow/react'
import { useRolodexEdit } from 'features/fde'
import { useGeckoNotifications } from 'features/notifications'
import {
  PfdData,
  PfdMetadata,
  PfdNode,
  ProcessFlowDiagramEntry,
} from 'features/processFlowDiagrams/types'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { createContext, useCallback, useEffect, useState } from 'react'
import { KeyedMutator } from 'swr'
import { RolodexEntryType } from 'types'

interface ProcessFlowDiagramBuilderContextProps {
  metadata: PfdMetadata
  processFlowEntry?: ProcessFlowDiagramEntry
  diagramID: string
  siteID: string
  pfd: PfdData
  setPfd: React.Dispatch<React.SetStateAction<PfdData>>
  onNodesChange: (changes: NodeChange<PfdNode>[]) => void
  onEdgesChange: (changes: EdgeChange[]) => void
  onConnect: (params: Connection) => void
  refreshProcessFlowData: KeyedMutator<ProcessFlowDiagramEntry[]>
  handleSaveToRolodex: () => void
  disableEditing: boolean
  setDisableEditing: React.Dispatch<React.SetStateAction<boolean>>
  onNodeClick?: (node: PfdNode) => void
  renderNodePopup?: (node: PfdNode, onClose: () => void) => React.ReactNode
}

export const ProcessFlowDiagramBuilderContext =
  createContext<ProcessFlowDiagramBuilderContextProps>(null!)

export const ProcessFlowDiagramBuilderDataLayer: React.FC<{
  children: React.ReactNode
  siteID: string
  diagramID: string
  viewOnly: boolean
  onNodeClick?: (node: PfdNode) => void
  renderNodePopup?: (node: PfdNode, onClose: () => void) => React.ReactNode
}> = (props) => {
  const {
    children,
    siteID,
    diagramID,
    viewOnly,
    onNodeClick,
    renderNodePopup,
  } = props
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { rolodexPut } = useRolodexEdit()

  const [disableEditing, setDisableEditing] = useState(viewOnly)
  const [pfd, setPfd] = useState<PfdData>({
    nodes: [],
    edges: [],
  })
  const { data: processFlow, mutate: refreshData } =
    useGetRolodexEntriesByLoadOptions<ProcessFlowDiagramEntry[]>({
      ids: [diagramID],
      tags: {
        site: siteID,
      },
      limit: 1,
    })

  const processFlowEntry = processFlow?.[0]
  const processFlowData = processFlowEntry?.entry.data

  useEffect(() => {
    if (processFlowData?.diagram) {
      setPfd(processFlowData.diagram)
    }
  }, [processFlowData])

  const handleSaveToRolodex = useCallback(() => {
    rolodexPut([
      {
        id: diagramID ?? '',
        type: RolodexEntryType.PROCESS_FLOW_DIAGRAM,
        data: {
          metadata: processFlowData?.metadata ?? {},
          diagram: pfd,
        },
        tags: processFlowEntry?.entry.tags ?? {},
      },
    ])
      .then(() => {
        successNotification('Process flow diagram saved successfully')
      })
      .catch(() => {
        errorNotification('Failed to save process flow diagram')
      })
  }, [
    diagramID,
    errorNotification,
    pfd,
    processFlowData?.metadata,
    processFlowEntry?.entry.tags,
    rolodexPut,
    successNotification,
  ])

  const onNodesChange = useCallback(
    (changes: NodeChange<PfdNode>[]) => {
      if (disableEditing) {
        return
      }
      setPfd((pfd) => ({
        ...pfd,
        nodes: applyNodeChanges(changes, pfd.nodes),
      }))
    },
    [disableEditing, setPfd]
  )
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (disableEditing) {
        return
      }
      setPfd((pfd) => ({
        ...pfd,
        edges: applyEdgeChanges(changes, pfd.edges),
      }))
    },
    [disableEditing, setPfd]
  )

  const onConnect = useCallback(
    (params: Connection) =>
      setPfd((pfd) => ({
        ...pfd,
        edges: addEdge(params, pfd.edges),
      })),
    [setPfd]
  )

  return (
    <ProcessFlowDiagramBuilderContext.Provider
      value={{
        metadata: processFlowData?.metadata ?? { name: '' },
        processFlowEntry,
        diagramID: diagramID ?? '',
        siteID: siteID ?? '',
        pfd,
        setPfd,
        onNodesChange,
        onEdgesChange,
        onConnect,
        refreshProcessFlowData: refreshData,
        handleSaveToRolodex,
        disableEditing,
        setDisableEditing,
        onNodeClick,
        renderNodePopup,
      }}>
      {children}
    </ProcessFlowDiagramBuilderContext.Provider>
  )
}
